import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  CheckCircle,
  Clock,
  AlertCircle,
  Loader,
  Wifi,
  Globe,
  X,
  Save,
  Trash2,
  Building2,
  User,
  CreditCard,
  Check,
  AlertTriangle,
  ChevronRight,
  Shield,
  Zap,
  Mail,
  Star,
  Users,
  Headphones,
  BarChart3,
  Settings,
  Database,
  Smartphone,
  Crown,
  Award,
  Sparkles
} from "lucide-react";

// Constants
const API_BASE_URL = "/api";
// Besto Admin API URL - central licensing and tenant management server
const BESTO_ADMIN_URL = process.env.REACT_APP_BESTO_ADMIN_URL || "/api";
const STORAGE_KEY = "bestohrms_registration_progress";
const SUBDOMAIN_REGEX = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
const MAX_PROGRESS_AGE = 24 * 60 * 60 * 1000; // 24 hours

// Plan keys aligned with besto_admin pricing
const PRICING_PLANS = {
  starter: {
    amount: 1999,
    durationInDays: 365,
    name: "Starter",
    description: "Perfect for small teams getting started",
    features: [
      "Up to 50 employees",
      "Core HR modules",
      "Email support",
      "Basic reporting",
      "Mobile app access",
      "Employee self-service"
    ],
    popular: false,
    icon: Users,
    color: "blue",
    savings: null
  },
  professional: {
    amount: 4999,
    durationInDays: 365,
    name: "Professional",
    description: "Ideal for growing companies",
    features: [
      "Up to 200 employees",
      "Advanced HR modules",
      "Priority support (24/7)",
      "Advanced analytics & reports",
      "Custom workflows",
      "API access",
      "Performance management",
      "Time & attendance tracking"
    ],
    popular: true,
    icon: Award,
    color: "green",
    savings: "Save 40%"
  },
  enterprise: {
    amount: 9999,
    durationInDays: 365,
    name: "Enterprise",
    description: "For large organizations with complex needs",
    features: [
      "Unlimited employees",
      "All premium modules",
      "Dedicated account manager",
      "Custom integrations",
      "Advanced security & compliance",
      "White-label options",
      "Custom training & onboarding",
      "SLA guarantees"
    ],
    popular: false,
    icon: Crown,
    color: "purple",
    savings: "Save 50%"
  },
};

const STEPS = [
  { 
    id: 1, 
    title: "Admin Setup", 
    icon: User, 
    description: "Create superadmin account" 
  },
  { 
    id: 2, 
    title: "Company Info", 
    icon: Building2, 
    description: "Business details" 
  },
  { 
    id: 3, 
    title: "Domain", 
    icon: Globe, 
    description: "Choose subdomain" 
  },
  { 
    id: 4, 
    title: "Subscription", 
    icon: CreditCard, 
    description: "Select plan" 
  },
  { 
    id: 5, 
    title: "Complete", 
    icon: CheckCircle, 
    description: "Setup complete" 
  },
];

// Custom Hooks
const useProgressPersistence = () => {
  const saveProgress = useCallback((data) => {
    try {
      const progressData = { ...data, timestamp: Date.now() };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(progressData));
    } catch (error) {
      console.error("Failed to save progress:", error);
    }
  }, []);

  const loadProgress = useCallback(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) return null;

      const progressData = JSON.parse(saved);
      if (Date.now() - progressData.timestamp > MAX_PROGRESS_AGE) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return progressData;
    } catch (error) {
      console.error("Failed to load progress:", error);
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
  }, []);

  const clearProgress = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return { saveProgress, loadProgress, clearProgress };
};

const useApiCall = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // apiCall with optional baseUrl parameter - defaults to API_BASE_URL
  // Use 'admin' as baseUrl to call besto_admin APIs
  const apiCall = useCallback(async (endpoint, method = "GET", data = null, isFormData = false, baseUrl = null) => {
    setLoading(true);
    setError(null);

    // Determine the base URL
    const url = baseUrl === 'admin' ? BESTO_ADMIN_URL : (baseUrl || API_BASE_URL);

    try {
      const config = {
        method,
        headers: isFormData ? {} : { "Content-Type": "application/json" },
      };

      if (data) {
        config.body = isFormData ? data : JSON.stringify(data);
      }

      const response = await fetch(`${url}${endpoint}`, config);
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.msg || result.error || result.message || "API call failed");
      }

      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { apiCall, loading, error, setError };
};

const useSubdomainValidation = () => {
  const [availability, setAvailability] = useState({
    isChecking: false,
    isAvailable: null,
    lastChecked: null
  });

  const checkAvailability = useCallback(async (subdomain, apiCall) => {
    if (!subdomain || subdomain.length < 3 || !SUBDOMAIN_REGEX.test(subdomain)) {
      setAvailability({ isChecking: false, isAvailable: null, lastChecked: null });
      return;
    }

    setAvailability(prev => ({ ...prev, isChecking: true }));

    try {
      // Call besto_admin API for subdomain check
      const result = await apiCall(`/tenants/check-subdomain/${subdomain}`, "GET", null, false, 'admin');
      setAvailability({
        isChecking: false,
        isAvailable: result.available,
        lastChecked: Date.now()
      });
    } catch (error) {
      setAvailability({
        isChecking: false,
        isAvailable: false,
        lastChecked: Date.now()
      });
    }
  }, []);

  const resetAvailability = useCallback(() => {
    setAvailability({ isChecking: false, isAvailable: null, lastChecked: null });
  }, []);

  return { availability, checkAvailability, resetAvailability };
};

// UI Components
const ProgressBar = React.memo(({ progress }) => (
  <div className="w-full bg-gray-100 h-3 rounded-full overflow-hidden shadow-inner">
    <div
      className="bg-gradient-to-r from-blue-500 to-blue-600 h-full rounded-full transition-all duration-700 ease-out shadow-sm relative overflow-hidden"
      style={{ width: `${progress}%` }}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse"></div>
    </div>
  </div>
));

const StepIndicator = React.memo(({ steps, currentStep }) => (
  <div className="flex items-center justify-between mb-10 overflow-x-auto">
    {steps.map((step, index) => {
      const Icon = step.icon;
      const isActive = currentStep === step.id;
      const isCompleted = currentStep > step.id;
      const isConnected = index < steps.length - 1;

      return (
        <React.Fragment key={step.id}>
          <div className="flex flex-col items-center relative min-w-0 flex-shrink-0">
            <div
              className={`
                relative w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center border-3 transition-all duration-300 shadow-lg
                ${
                  isCompleted
                    ? "bg-green-500 border-green-500 text-white shadow-green-200"
                    : isActive
                    ? "bg-blue-500 border-blue-500 text-white shadow-blue-200"
                    : "bg-white border-gray-300 text-gray-400 shadow-gray-100"
                }
              `}
            >
              {isCompleted ? (
                <Check className="w-6 h-6 sm:w-7 sm:h-7" />
              ) : (
                <Icon className="w-6 h-6 sm:w-7 sm:h-7" />
              )}
              {isActive && (
                <div className="absolute inset-0 rounded-full border-2 border-blue-300 animate-ping"></div>
              )}
            </div>
            <div className="mt-3 text-center min-w-0">
              <div
                className={`text-xs sm:text-sm font-semibold ${
                  isActive ? "text-blue-600" : isCompleted ? "text-green-600" : "text-gray-500"
                }`}
              >
                {step.title}
              </div>
              <div className="text-xs text-gray-400 max-w-20 hidden sm:block">{step.description}</div>
            </div>
          </div>
          {isConnected && (
            <div className="flex-1 h-0.5 mx-3 sm:mx-6 mb-10 relative min-w-8">
              <div
                className={`h-full transition-all duration-500 ${
                  isCompleted ? "bg-green-500" : "bg-gray-200"
                }`}
              />
            </div>
          )}
        </React.Fragment>
      );
    })}
  </div>
));

const Alert = React.memo(({ type, message, onDismiss }) => {
  const styles = {
    success: "bg-green-50 border-green-200 text-green-800",
    error: "bg-red-50 border-red-200 text-red-800",
    warning: "bg-yellow-50 border-yellow-200 text-yellow-800",
    info: "bg-blue-50 border-blue-200 text-blue-800"
  };

  const icons = {
    success: CheckCircle,
    error: AlertCircle,
    warning: AlertTriangle,
    info: Clock
  };

  const Icon = icons[type];

  return (
    <div className={`border rounded-xl p-4 flex items-start space-x-3 shadow-sm ${styles[type]}`}>
      <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm font-medium">{message}</p>
      </div>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
});

const LoadingButton = React.memo(({ 
  loading, 
  disabled, 
  onClick, 
  children, 
  variant = "primary",
  size = "md",
  className = "",
  ...props 
}) => {
  const baseClasses = "inline-flex items-center justify-center font-semibold rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5";
  
  const variants = {
    primary: "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white focus:ring-blue-500 disabled:from-blue-400 disabled:to-blue-500",
    secondary: "bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-800 focus:ring-gray-500 disabled:from-gray-100 disabled:to-gray-100",
    success: "bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white focus:ring-green-500 disabled:from-green-400 disabled:to-green-500",
    outline: "border-2 border-gray-300 bg-white hover:bg-gray-50 text-gray-700 focus:ring-gray-500"
  };

  const sizes = {
    sm: "px-4 py-2 text-sm",
    md: "px-6 py-3 text-sm",
    lg: "px-8 py-4 text-base"
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className} ${
        (disabled || loading) ? "cursor-not-allowed opacity-60 transform-none shadow-md" : ""
      }`}
      {...props}
    >
      {loading && <Loader className="w-4 h-4 mr-2 animate-spin" />}
      {children}
    </button>
  );
});

const FormField = React.memo(({ 
  label, 
  error, 
  required, 
  children, 
  description,
  className = "" 
}) => (
  <div className={`space-y-2 ${className}`}>
    <label className="block text-sm font-semibold text-gray-700">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    {description && (
      <p className="text-xs text-gray-500">{description}</p>
    )}
    {children}
    {error && (
      <p className="text-sm text-red-600 flex items-center bg-red-50 p-2 rounded-lg">
        <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
        {error}
      </p>
    )}
  </div>
));

const ResumeProgressDialog = React.memo(({ 
  show, 
  savedData, 
  onResume, 
  onStartFresh, 
  onDismiss 
}) => {
  if (!show || !savedData) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 transform transition-all duration-300 scale-100">
        <div className="flex items-center mb-6">
          <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mr-4">
            <Save className="w-7 h-7 text-blue-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">Resume Registration?</h3>
            <p className="text-sm text-gray-500">Continue where you left off</p>
          </div>
        </div>

        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-5 mb-6">
          <div className="space-y-3 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-gray-600 font-medium">Progress:</span>
              <span className="font-semibold text-blue-600">Step {savedData.step} of 5</span>
            </div>
            {savedData.companyData?.name && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600 font-medium">Company:</span>
                <span className="font-semibold text-gray-900">{savedData.companyData.name}</span>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-gray-600 font-medium">Saved:</span>
              <span className="font-semibold text-gray-900">
                {new Date(savedData.timestamp).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>

        <div className="flex space-x-3">
          <LoadingButton
            onClick={onResume}
            variant="primary"
            className="flex-1"
          >
            <Save className="w-4 h-4 mr-2" />
            Resume
          </LoadingButton>
          <LoadingButton
            onClick={onStartFresh}
            variant="outline"
            className="flex-1"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Start Fresh
          </LoadingButton>
        </div>
      </div>
    </div>
  );
});

// Main Component
const RegisterCompanyFlow = () => {
  // State Management
  const [currentStep, setCurrentStep] = useState(1);
  const [tenantId, setTenantId] = useState(null); // besto_admin tenant ID
  const [companyId, setCompanyId] = useState(null);
  const [adminUserId, setAdminUserId] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const [savedProgressData, setSavedProgressData] = useState(null);
  const [licenseInfo, setLicenseInfo] = useState(null); // License info after payment

  // Form Data States
  const [adminData, setAdminData] = useState({
    name: "",
    email: "",
    role: "superadmin",
  });

  const [companyData, setCompanyData] = useState({
    name: "",
    industry: "",
    email: "",
    phone: "",
    address: "",
    logo: null,
  });

  const [domainData, setDomainData] = useState({
    type: "subdomain", // 'subdomain' or 'custom'
    subdomain: "",
    customDomain: "",
  });

  const [selectedPlan, setSelectedPlan] = useState("");

  // Device Authentication State
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [deviceConnected, setDeviceConnected] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState({
    macAddress: "",
    uniqueId: ""
  });

  // Custom Hooks
  const { saveProgress, loadProgress, clearProgress } = useProgressPersistence();
  const { apiCall, loading, error, setError } = useApiCall();
  const { availability, checkAvailability, resetAvailability } = useSubdomainValidation();

  // Computed Values
  const progressPercentage = useMemo(() => currentStep * 20, [currentStep]);

  // Notification System
  const addAlert = useCallback((type, message, duration = 5000) => {
    const id = Date.now();
    setAlerts(prev => [...prev, { id, type, message }]);
    
    if (duration > 0) {
      setTimeout(() => {
        setAlerts(prev => prev.filter(alert => alert.id !== id));
      }, duration);
    }
  }, []);

  const removeAlert = useCallback((id) => {
    setAlerts(prev => prev.filter(alert => alert.id !== id));
  }, []);

  // Progress Management
  const updateProgress = useCallback((step) => {
    setCurrentStep(step);
  }, []);

  const goToNextStep = useCallback(() => {
    if (currentStep < 5) {
      updateProgress(currentStep + 1);
    }
  }, [currentStep, updateProgress]);

  const goToPreviousStep = useCallback(() => {
    if (currentStep > 1) {
      updateProgress(currentStep - 1);
    }
  }, [currentStep, updateProgress]);


  // Auto-save functionality
  useEffect(() => {
    if (currentStep > 1 || tenantId) {
      const timeoutId = setTimeout(() => {
        const progressData = {
          step: currentStep,
          tenantId,
          companyId,
          adminUserId,
          selectedPlan,
          adminData,
          companyData: { ...companyData, logo: null },
          domainData,
          licenseInfo,
        };
        saveProgress(progressData);
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [currentStep, tenantId, companyId, adminUserId, selectedPlan, adminData, companyData, domainData, licenseInfo, saveProgress]);
  
  // Load saved progress on mount
  useEffect(() => {
    const saved = loadProgress();
    if (saved && saved.step > 1) {
      setSavedProgressData(saved);
      setShowResumeDialog(true);
    }
  }, [loadProgress]);

  // Resume progress handler
  const handleResumeProgress = useCallback(() => {
    if (savedProgressData) {
      setCurrentStep(savedProgressData.step);
      setTenantId(savedProgressData.tenantId || null);
      setCompanyId(savedProgressData.companyId);
      setAdminUserId(savedProgressData.adminUserId);
      setSelectedPlan(savedProgressData.selectedPlan || "");
      setAdminData(prev => ({ ...prev, ...savedProgressData.adminData }));
      setCompanyData(prev => ({ ...prev, ...savedProgressData.companyData }));
      setDomainData(prev => ({ ...prev, ...savedProgressData.domainData }));
      setLicenseInfo(savedProgressData.licenseInfo || null);

      setShowResumeDialog(false);
      addAlert("success", "Progress restored successfully!");
    }
  }, [savedProgressData, addAlert]);

  const handleStartFresh = useCallback(() => {
    clearProgress();
    setShowResumeDialog(false);
    addAlert("info", "Starting fresh registration");
  }, [clearProgress, addAlert]);

  // Step 1: Superadmin Info Collection (simplified - no email verification yet)
  // Email verification will happen after admin provisions the hrms_back instance
  const handleAdminSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError(null);

    if (!adminData.name || !adminData.email) {
      addAlert("error", "Name and email are required for superadmin creation");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(adminData.email)) {
      addAlert("error", "Please enter a valid email address");
      return;
    }

    // Just validate and proceed - no API call needed yet
    // The actual tenant registration happens in Step 2 with company info
    addAlert("success", "Superadmin details saved! Proceeding to company information.");
    goToNextStep();
  }, [adminData, addAlert, goToNextStep, setError]);

  // Step 2: Company Details - Register tenant with besto_admin
  const handleCompanySubmit = useCallback(async (e) => {
    e.preventDefault();
    setError(null);

    if (!companyData.name || !companyData.email) {
      addAlert("error", "Company name and email are required");
      return;
    }

    try {
      // Create tenant in besto_admin with company and superadmin info
      const tenantPayload = {
        companyName: companyData.name,
        industry: companyData.industry,
        email: companyData.email,
        phone: companyData.phone,
        address: companyData.address,
        superadmin: {
          name: adminData.name,
          email: adminData.email,
          phone: companyData.phone
        }
      };

      // Call besto_admin API to create tenant
      const result = await apiCall("/tenants", "POST", tenantPayload, false, 'admin');

      setTenantId(result.tenant.tenantId);
      setCompanyId(result.tenant.tenantId); // Use tenantId as companyId for compatibility

      addAlert("success", result.message || "Company registered successfully!");
      goToNextStep();
    } catch (err) {
      addAlert("error", err.message || "Company registration failed");
    }
  }, [companyData, adminData, apiCall, addAlert, goToNextStep, setError]);

  // Step 3: Domain Setup - Update tenant with subdomain or custom domain
  const handleSubdomainSubmit = useCallback(async () => {
    if (domainData.type === 'subdomain') {
      if (!domainData.subdomain) {
        addAlert("error", "Please enter a subdomain");
        return;
      }

      if (!SUBDOMAIN_REGEX.test(domainData.subdomain)) {
        addAlert("error", "Subdomain must contain only lowercase letters, numbers, and hyphens");
        return;
      }

      if (availability.isAvailable === false) {
        addAlert("error", "This subdomain is not available. Please choose another");
        return;
      }
    } else {
      // Custom domain validation
      if (!domainData.customDomain) {
        addAlert("error", "Please enter your custom domain");
        return;
      }

      // Basic domain validation
      const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/;
      if (!domainRegex.test(domainData.customDomain)) {
        addAlert("error", "Please enter a valid domain (e.g., hr.yourcompany.com)");
        return;
      }
    }

    try {
      // Update tenant with domain info in besto_admin using the public setup-domain endpoint
      const domainPayload = domainData.type === 'subdomain'
        ? {
            type: 'subdomain',
            subdomain: domainData.subdomain
          }
        : {
            type: 'custom',
            customDomain: domainData.customDomain.toLowerCase(),
            isConfigured: false // Will be configured by admin later
          };

      const result = await apiCall(`/tenants/${tenantId}/setup-domain`, "PUT", {
        domain: domainPayload
      }, false, 'admin');

      const successMsg = domainData.type === 'subdomain'
        ? "Subdomain configured successfully!"
        : "Custom domain request submitted! Our team will contact you for setup.";

      addAlert("success", result.message || successMsg);
      goToNextStep();
    } catch (err) {
      addAlert("error", err.message || "Domain setup failed");
    }
  }, [domainData, availability.isAvailable, tenantId, apiCall, addAlert, goToNextStep]);

  // Step 4: Plan Selection and Payment via besto_admin
  const handlePlanSelect = useCallback(async (planKey) => {
    try {
      setSelectedPlan(planKey);

      // Create payment order via besto_admin
      const orderResult = await apiCall("/payments/create-order", "POST", {
        plan: planKey,
        tenantId: tenantId,
      }, false, 'admin');

      const { order, paymentId, key } = orderResult;

      // Initialize Razorpay
      const options = {
        key: key || process.env.REACT_APP_RAZORPAY_KEY || "rzp_test_BzMVkQvdeBvOhG",
        amount: order.amount,
        currency: order.currency,
        order_id: order.id,
        name: "BestoHRMS Subscription",
        description: `${PRICING_PLANS[planKey].name} plan`,
        handler: async (response) => {
          try {
            // Verify payment with besto_admin
            const verifyResult = await apiCall("/payments/verify", "POST", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              paymentId: paymentId
            }, false, 'admin');

            // Store license info for display
            if (verifyResult.payment) {
              setLicenseInfo({
                invoiceNumber: verifyResult.payment.invoiceNumber,
                status: verifyResult.payment.status
              });
            }

            addAlert("success", "🎉 Payment successful! Your subscription is now active.");
            clearProgress();
            setCurrentStep(5);
          } catch (error) {
            addAlert("error", "Payment succeeded, but verification failed. Please contact support.");
          }
        },
        prefill: {
          email: companyData.email,
          contact: companyData.phone,
        },
        theme: {
          color: "#3B82F6",
        },
        modal: {
          ondismiss: function() {
            addAlert("info", "Payment cancelled. You can try again.");
          }
        }
      };

      const razor = new window.Razorpay(options);
      razor.open();
    } catch (error) {
      addAlert("error", error.message || "Failed to initiate payment");
    }
  }, [tenantId, companyData.email, companyData.phone, apiCall, addAlert, clearProgress]);

  // Subdomain availability check with debouncing
  useEffect(() => {
    if (domainData.subdomain) {
      const timer = setTimeout(() => {
        checkAvailability(domainData.subdomain, apiCall);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      resetAvailability();
    }
  }, [domainData.subdomain, checkAvailability, resetAvailability, apiCall]);

  // Render Methods - Step 1: Simplified Superadmin Form
  const renderSuperadminForm = () => (
    <div className="space-y-6 sm:space-y-8">
      <div className="text-center mb-8 sm:mb-10">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <User className="w-8 h-8 text-blue-600" />
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Create Superadmin Account</h2>
        <p className="text-gray-600 text-base sm:text-lg px-4">
          Set up the primary administrator for your organization
        </p>
      </div>

      <form onSubmit={handleAdminSubmit} className="space-y-6 sm:space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <FormField label="Full Name" required>
            <input
              type="text"
              value={adminData.name}
              onChange={(e) => setAdminData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm text-sm sm:text-base"
              placeholder="Enter full name"
              required
            />
          </FormField>

          <FormField label="Email Address" required>
            <input
              type="email"
              value={adminData.email}
              onChange={(e) => setAdminData(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm text-sm sm:text-base"
              placeholder="Enter email address"
              required
            />
          </FormField>
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 sm:p-8 border border-blue-100">
          <div className="flex items-center mb-4">
            <Shield className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0" />
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900">What happens next?</h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-center text-sm text-gray-600">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
              <span>Complete company registration and payment</span>
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
              <span>Your HRMS portal will be provisioned within 24-48 hours</span>
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
              <span>Superadmin will receive login credentials via email</span>
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
              <span>Choose authentication method (Bluetooth or Email OTP)</span>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 sm:p-6">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-yellow-800 mb-1">Important Note</h4>
              <p className="text-sm text-yellow-700">
                This email will be used for superadmin login. Please ensure it's a valid email address
                that you have access to.
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-4 sm:pt-6">
          <LoadingButton
            type="submit"
            loading={loading}
            disabled={!adminData.name || !adminData.email}
            variant="primary"
            size="lg"
            className="w-full sm:w-auto"
          >
            Continue to Company Info
            <ChevronRight className="w-4 h-4 ml-2" />
          </LoadingButton>
        </div>
      </form>
    </div>
  );

  const renderCompanyForm = () => (
    <div className="space-y-6 sm:space-y-8">
      <div className="text-center mb-8 sm:mb-10">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Building2 className="w-8 h-8 text-blue-600" />
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Company Information</h2>
        <p className="text-gray-600 text-base sm:text-lg px-4">Tell us about your organization</p>
      </div>

      <form onSubmit={handleCompanySubmit} className="space-y-6 sm:space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <FormField label="Company Name" required>
            <input
              type="text"
              value={companyData.name}
              onChange={(e) => setCompanyData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm text-sm sm:text-base"
              placeholder="Enter company name"
            />
          </FormField>

          <FormField label="Industry">
            <input
              type="text"
              value={companyData.industry}
              onChange={(e) => setCompanyData(prev => ({ ...prev, industry: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm text-sm sm:text-base"
              placeholder="e.g., Technology, Healthcare"
            />
          </FormField>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <FormField label="Company Email" required>
            <input
              type="email"
              value={companyData.email}
              onChange={(e) => setCompanyData(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm text-sm sm:text-base"
              placeholder="company@example.com"
            />
          </FormField>

          <FormField label="Phone Number">
            <input
              type="tel"
              value={companyData.phone}
              onChange={(e) => setCompanyData(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm text-sm sm:text-base"
              placeholder="+91 98765 43210"
            />
          </FormField>
        </div>

        <FormField label="Address">
          <textarea
            value={companyData.address}
            onChange={(e) => setCompanyData(prev => ({ ...prev, address: e.target.value }))}
            rows={3}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none shadow-sm text-sm sm:text-base"
            placeholder="Enter company address"
          />
        </FormField>

        <FormField 
          label="Company Logo" 
          description="Upload your company logo (PNG, JPG, or SVG - Max 5MB)"
        >
          <input
            type="file"
            onChange={(e) => setCompanyData(prev => ({ ...prev, logo: e.target.files[0] }))}
            accept="image/*"
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm text-sm sm:text-base file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
        </FormField>

        <div className="flex flex-col sm:flex-row justify-between gap-4 pt-6 sm:pt-8">
          <LoadingButton
            onClick={goToPreviousStep}
            variant="outline"
            size="lg"
            className="w-full sm:w-auto order-2 sm:order-1"
          >
            Back
          </LoadingButton>
          <LoadingButton
            type="submit"
            loading={loading}
            variant="primary"
            size="lg"
            className="w-full sm:w-auto order-1 sm:order-2"
          >
            Continue to Domain Setup
            <ChevronRight className="w-4 h-4 ml-2" />
          </LoadingButton>
        </div>
      </form>
    </div>
  );

  const renderDomainSetup = () => {
    const getAvailabilityIcon = () => {
      if (availability.isChecking) {
        return <Loader className="w-5 h-5 text-blue-500 animate-spin" />;
      }
      if (availability.isAvailable === true) {
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      }
      if (availability.isAvailable === false) {
        return <X className="w-5 h-5 text-red-500" />;
      }
      return null;
    };

    const isSubdomainValid = domainData.type === 'subdomain' &&
      domainData.subdomain &&
      domainData.subdomain.length >= 3 &&
      SUBDOMAIN_REGEX.test(domainData.subdomain) &&
      availability.isAvailable === true;

    const isCustomDomainValid = domainData.type === 'custom' &&
      domainData.customDomain &&
      /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/.test(domainData.customDomain);

    const canProceed = isSubdomainValid || isCustomDomainValid;

    return (
      <div className="space-y-6 sm:space-y-8">
        <div className="text-center mb-8 sm:mb-10">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Globe className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Configure Your Domain</h2>
          <p className="text-gray-600 text-base sm:text-lg px-4">Choose how you want to access your HRMS portal</p>
        </div>

        {/* Domain Type Toggle */}
        <div className="flex justify-center mb-6">
          <div className="bg-gray-100 p-1 rounded-xl flex items-center">
            <button
              onClick={() => setDomainData(prev => ({ ...prev, type: 'subdomain' }))}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                domainData.type === 'subdomain'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              BestoHRMS Subdomain
            </button>
            <button
              onClick={() => setDomainData(prev => ({ ...prev, type: 'custom' }))}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                domainData.type === 'custom'
                  ? 'bg-white text-purple-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Custom Domain
            </button>
          </div>
        </div>

        {/* Subdomain Configuration */}
        {domainData.type === 'subdomain' && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 sm:p-8 border border-blue-100">
            <div className="flex items-center mb-4 sm:mb-6">
              <Globe className="w-7 h-7 text-blue-600 mr-3 flex-shrink-0" />
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Subdomain Configuration</h3>
            </div>

            <p className="text-gray-600 mb-6 sm:mb-8 text-base sm:text-lg">
              Get a free subdomain on bestohrms.com. Your portal will be ready immediately after setup.
            </p>

            <FormField
              label="Enter your preferred subdomain"
              description="Minimum 3 characters • Only lowercase letters, numbers, and hyphens allowed"
            >
              <div className="flex items-center border-2 border-gray-300 rounded-xl bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all shadow-sm">
                <input
                  type="text"
                  value={domainData.subdomain}
                  onChange={(e) => setDomainData(prev => ({
                    ...prev,
                    subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')
                  }))}
                  className="flex-1 px-4 py-3 sm:py-4 border-0 outline-none text-base sm:text-lg font-medium"
                  placeholder="yourcompany"
                  maxLength={63}
                />
                <div className="flex items-center px-3 sm:px-4 py-3 sm:py-4 bg-gray-50 text-gray-600 font-medium border-l border-gray-200 text-sm sm:text-base">
                  .bestohrms.com
                </div>
                <div className="px-3 sm:px-4 py-3 sm:py-4">
                  {getAvailabilityIcon()}
                </div>
              </div>

              <div className="mt-3 space-y-2">
                {domainData.subdomain && !SUBDOMAIN_REGEX.test(domainData.subdomain) && (
                  <p className="text-sm text-red-600 flex items-center">
                    <X className="w-4 h-4 mr-2 flex-shrink-0" />
                    Invalid format. Use only lowercase letters, numbers, and hyphens.
                  </p>
                )}

                {availability.isAvailable === true && (
                  <p className="text-sm text-green-600 flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                    Great! This subdomain is available.
                  </p>
                )}

                {availability.isAvailable === false && (
                  <p className="text-sm text-red-600 flex items-center">
                    <X className="w-4 h-4 mr-2 flex-shrink-0" />
                    This subdomain is already taken. Please try another.
                  </p>
                )}

                {domainData.subdomain && domainData.subdomain.length < 3 && (
                  <p className="text-sm text-yellow-600 flex items-center">
                    <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0" />
                    Subdomain must be at least 3 characters long.
                  </p>
                )}
              </div>
            </FormField>

            {domainData.subdomain && availability.isAvailable === true && (
              <div className="mt-6 sm:mt-8 p-4 sm:p-6 bg-white border-2 border-green-200 rounded-xl">
                <div className="flex items-center mb-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mr-3 flex-shrink-0" />
                  <h4 className="text-lg font-semibold text-gray-900">Preview Your URL</h4>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border">
                  <p className="text-sm text-gray-600 mb-1">Your HRMS portal will be accessible at:</p>
                  <p className="text-lg sm:text-xl font-bold text-blue-600 break-all">
                    https://{domainData.subdomain}.bestohrms.com
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Custom Domain Configuration */}
        {domainData.type === 'custom' && (
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 sm:p-8 border border-purple-100">
            <div className="flex items-center mb-4 sm:mb-6">
              <Globe className="w-7 h-7 text-purple-600 mr-3 flex-shrink-0" />
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Custom Domain</h3>
            </div>

            <p className="text-gray-600 mb-6 sm:mb-8 text-base sm:text-lg">
              Host your HRMS on your own domain for a professional branded experience.
            </p>

            <FormField
              label="Enter your custom domain"
              description="e.g., hr.yourcompany.com or hrms.yourcompany.com"
            >
              <div className="flex items-center border-2 border-gray-300 rounded-xl bg-white focus-within:ring-2 focus-within:ring-purple-500 focus-within:border-purple-500 transition-all shadow-sm">
                <div className="flex items-center px-3 sm:px-4 py-3 sm:py-4 bg-gray-50 text-gray-600 font-medium border-r border-gray-200 text-sm sm:text-base">
                  https://
                </div>
                <input
                  type="text"
                  value={domainData.customDomain}
                  onChange={(e) => setDomainData(prev => ({
                    ...prev,
                    customDomain: e.target.value.toLowerCase().replace(/[^a-z0-9.-]/g, '')
                  }))}
                  className="flex-1 px-4 py-3 sm:py-4 border-0 outline-none text-base sm:text-lg font-medium"
                  placeholder="hr.yourcompany.com"
                />
                {isCustomDomainValid && (
                  <div className="px-3 sm:px-4 py-3 sm:py-4">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  </div>
                )}
              </div>
            </FormField>

            {isCustomDomainValid && (
              <div className="mt-6 sm:mt-8 p-4 sm:p-6 bg-white border-2 border-purple-200 rounded-xl">
                <div className="flex items-center mb-3">
                  <CheckCircle className="w-6 h-6 text-purple-600 mr-3 flex-shrink-0" />
                  <h4 className="text-lg font-semibold text-gray-900">Your Custom Domain</h4>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border mb-4">
                  <p className="text-sm text-gray-600 mb-1">Your HRMS portal will be accessible at:</p>
                  <p className="text-lg sm:text-xl font-bold text-purple-600 break-all">
                    https://{domainData.customDomain}
                  </p>
                </div>
              </div>
            )}

            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-amber-800 mb-1">Custom Domain Setup</h4>
                  <p className="text-sm text-amber-700">
                    After registration, our team will contact you to configure DNS settings for your custom domain.
                    Your HRMS will be available on the subdomain immediately until custom domain setup is complete.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row justify-between gap-4 pt-6 sm:pt-8">
          <LoadingButton
            onClick={goToPreviousStep}
            variant="outline"
            size="lg"
            className="w-full sm:w-auto order-2 sm:order-1"
          >
            Back
          </LoadingButton>
          <LoadingButton
            onClick={handleSubdomainSubmit}
            loading={loading}
            disabled={!canProceed}
            variant="primary"
            size="lg"
            className="w-full sm:w-auto order-1 sm:order-2"
          >
            {domainData.type === 'subdomain' ? 'Setup Subdomain' : 'Request Custom Domain'}
            <ChevronRight className="w-4 h-4 ml-2" />
          </LoadingButton>
        </div>
      </div>
    );
  };

  // COMPLETELY REDESIGNED SUBSCRIPTION FORM
  const renderPlanSelection = () => (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
          <Sparkles className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
          Choose Your Perfect Plan
        </h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Select the ideal plan for your organization's needs. Upgrade or downgrade anytime.
        </p>
      </div>

      {/* Billing Toggle */}
      <div className="flex items-center justify-center mb-8">
        <div className="bg-gray-100 p-1 rounded-full flex items-center">
          <button className="px-6 py-2 rounded-full bg-white text-gray-900 font-medium shadow-sm">
            Annual
          </button>
          <button className="px-6 py-2 rounded-full text-gray-600 font-medium">
            Monthly
          </button>
        </div>
        <div className="ml-3 flex items-center">
          <span className="bg-green-100 text-green-800 text-xs font-semibold px-2 py-1 rounded-full">
            Save up to 30%
          </span>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
        {Object.entries(PRICING_PLANS).map(([planKey, plan]) => {
          const IconComponent = plan.icon;
          const isPopular = plan.popular;
          
          return (
            <div
              key={planKey}
              className={`relative rounded-3xl transition-all duration-300 hover:scale-105 ${
                isPopular
                  ? "bg-gradient-to-b from-blue-600 to-blue-700 text-white shadow-2xl transform scale-105"
                  : "bg-white border-2 border-gray-200 hover:border-blue-300 shadow-lg hover:shadow-xl"
              }`}
            >
              {/* Popular Badge */}
              {isPopular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                  <div className="bg-gradient-to-r from-orange-400 to-pink-500 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                    ⭐ Most Popular
                  </div>
                </div>
              )}

              {/* Savings Badge */}
              {plan.savings && (
                <div className="absolute -top-2 -right-2 z-10">
                  <div className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                    {plan.savings}
                  </div>
                </div>
              )}

              <div className="p-8">
                {/* Plan Header */}
                <div className="text-center mb-8">
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
                    isPopular ? "bg-white/20" : "bg-blue-50"
                  }`}>
                    <IconComponent className={`w-8 h-8 ${
                      isPopular ? "text-white" : "text-blue-600"
                    }`} />
                  </div>
                  
                  <h3 className={`text-2xl font-bold mb-2 ${
                    isPopular ? "text-white" : "text-gray-900"
                  }`}>
                    {plan.name}
                  </h3>
                  
                  <p className={`text-sm mb-6 ${
                    isPopular ? "text-blue-100" : "text-gray-600"
                  }`}>
                    {plan.description}
                  </p>
                  
                  {/* Price */}
                  <div className="mb-6">
                    <div className="flex items-baseline justify-center">
                      <span className={`text-4xl font-bold ${
                        isPopular ? "text-white" : "text-gray-900"
                      }`}>
                        ₹{(plan.amount / 12).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </span>
                      <span className={`ml-2 text-sm ${
                        isPopular ? "text-blue-100" : "text-gray-600"
                      }`}>
                        /month
                      </span>
                    </div>
                    <div className={`text-sm mt-1 ${
                      isPopular ? "text-blue-100" : "text-gray-500"
                    }`}>
                      Billed annually (₹{plan.amount.toLocaleString('en-IN')})
                    </div>
                  </div>
                </div>

                {/* Features List */}
                <div className="space-y-4 mb-8">
                  {plan.features.map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <CheckCircle className={`w-5 h-5 mt-0.5 mr-3 flex-shrink-0 ${
                        isPopular ? "text-green-300" : "text-green-500"
                      }`} />
                      <span className={`text-sm ${
                        isPopular ? "text-blue-50" : "text-gray-700"
                      }`}>
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <LoadingButton
                  onClick={() => handlePlanSelect(planKey)}
                  loading={loading && selectedPlan === planKey}
                  variant={isPopular ? "secondary" : "primary"}
                  size="lg"
                  className={`w-full ${
                    isPopular 
                      ? "bg-white text-blue-600 hover:bg-blue-50 shadow-lg" 
                      : ""
                  }`}
                >
                  {loading && selectedPlan === planKey ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Get Started with {plan.name}
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </LoadingButton>

                {/* Trial Info */}
                <div className={`text-center mt-4 text-xs ${
                  isPopular ? "text-blue-100" : "text-gray-500"
                }`}>
                  14-day free trial • No credit card required
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Features Comparison */}
      <div className="bg-gray-50 rounded-2xl p-8 mt-12">
        <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
          What's included in every plan
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: Shield, text: "Enterprise Security" },
            { icon: Database, text: "Daily Backups" },
            { icon: Headphones, text: "24/7 Support" },
            { icon: Smartphone, text: "Mobile Apps" },
            { icon: BarChart3, text: "Analytics Dashboard" },
            { icon: Settings, text: "Custom Integrations" },
            { icon: CheckCircle, text: "99.9% Uptime SLA" },
            { icon: Users, text: "Team Collaboration" }
          ].map((item, index) => (
            <div key={index} className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <item.icon className="w-4 h-4 text-blue-600" />
              </div>
              <span className="text-sm font-medium text-gray-700">{item.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-white rounded-2xl p-8 border border-gray-200">
        <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
          Frequently Asked Questions
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Can I change plans later?</h4>
            <p className="text-sm text-gray-600">Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.</p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Is there a setup fee?</h4>
            <p className="text-sm text-gray-600">No setup fees! We'll have you up and running in under an hour.</p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">What payment methods do you accept?</h4>
            <p className="text-sm text-gray-600">We accept all major credit cards, debit cards, and net banking.</p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Is my data secure?</h4>
            <p className="text-sm text-gray-600">Absolutely. We use bank-level encryption and follow SOC 2 compliance standards.</p>
          </div>
        </div>
      </div>

      {/* Money Back Guarantee */}
      <div className="text-center bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-8">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Shield className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">30-Day Money-Back Guarantee</h3>
        <p className="text-gray-600">
          Not satisfied? Get a full refund within 30 days, no questions asked.
        </p>
      </div>

      {/* Navigation */}
      <div className="flex flex-col sm:flex-row justify-between gap-4 pt-8">
        <LoadingButton
          onClick={goToPreviousStep}
          variant="outline"
          size="lg"
          className="w-full sm:w-auto order-2 sm:order-1"
        >
          <ChevronRight className="w-4 h-4 mr-2 rotate-180" />
          Back to Domain Setup
        </LoadingButton>
        <div className="order-1 sm:order-2 text-center sm:text-right">
          <p className="text-sm text-gray-500">
            Need a custom plan? <a href="mailto:sales@bestohrms.com" className="text-blue-600 hover:underline">Contact our sales team</a>
          </p>
        </div>
      </div>
    </div>
  );

  const renderSuccessPage = () => (
    <div className="text-center space-y-6 sm:space-y-8">
      <div className="mb-6 sm:mb-8">
        <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-12 h-12 text-green-600" />
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
          Registration Completed Successfully!
        </h2>
        <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">
          Your BestoHRMS tenant registration is complete. Your license has been generated.
        </p>
      </div>

      {/* Tenant ID Card */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 sm:p-8 border border-green-200 max-w-2xl mx-auto">
        <div className="flex items-center justify-center mb-4">
          <Shield className="w-8 h-8 text-green-600 mr-3" />
          <h3 className="text-xl font-semibold text-gray-900">Your Tenant Details</h3>
        </div>

        <div className="bg-white rounded-xl p-4 border border-green-100 mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
            <div>
              <p className="text-sm text-gray-500">Tenant ID</p>
              <p className="font-mono font-bold text-gray-900">{tenantId}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Plan</p>
              <p className="font-semibold text-gray-900">{PRICING_PLANS[selectedPlan]?.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Domain</p>
              <p className="font-semibold text-blue-600">
                {domainData.type === 'custom'
                  ? domainData.customDomain
                  : `${domainData.subdomain}.bestohrms.com`}
              </p>
              {domainData.type === 'custom' && (
                <p className="text-xs text-amber-600 mt-1">Custom domain setup pending</p>
              )}
            </div>
            {licenseInfo?.invoiceNumber && (
              <div>
                <p className="text-sm text-gray-500">Invoice</p>
                <p className="font-semibold text-gray-900">{licenseInfo.invoiceNumber}</p>
              </div>
            )}
          </div>
        </div>

        <div className="text-sm text-green-700">
          <CheckCircle className="w-4 h-4 inline mr-1" />
          License generated and activated
        </div>
      </div>

      {/* Setup Progress */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 sm:p-8 border border-blue-100 max-w-2xl mx-auto">
        <div className="flex items-center justify-center mb-6">
          <Clock className="w-8 h-8 text-blue-600 mr-3 flex-shrink-0" />
          <h3 className="text-xl font-semibold text-gray-900">Portal Provisioning</h3>
        </div>

        <div className="space-y-4 text-left">
          <div className="flex items-start">
            <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-gray-900">Registration Complete</p>
              <p className="text-sm text-gray-600">Your tenant account has been created</p>
            </div>
          </div>

          <div className="flex items-start">
            <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-gray-900">Payment Verified</p>
              <p className="text-sm text-gray-600">Your {PRICING_PLANS[selectedPlan]?.name} subscription is active</p>
            </div>
          </div>

          <div className="flex items-start">
            <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-gray-900">License Generated</p>
              <p className="text-sm text-gray-600">Your license key has been created</p>
            </div>
          </div>

          <div className="flex items-start">
            <div className="w-5 h-5 border-2 border-blue-500 rounded-full mr-3 mt-0.5 flex-shrink-0 flex items-center justify-center">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
            </div>
            <div>
              <p className="font-medium text-gray-900">Server Provisioning</p>
              <p className="text-sm text-gray-600">
                Our team is setting up your dedicated HRMS instance
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 sm:mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
          <div className="flex items-center mb-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0" />
            <span className="font-medium text-yellow-800">Setup Time</span>
          </div>
          <p className="text-sm text-yellow-700">
            Your HRMS portal at <strong>{domainData.type === 'custom' ? domainData.customDomain : `${domainData.subdomain}.bestohrms.com`}</strong> will be provisioned within <strong>24-48 hours</strong>.
            You'll receive an email with login credentials once your portal is ready.
            {domainData.type === 'custom' && (
              <span className="block mt-2">Our team will contact you to complete the custom domain setup.</span>
            )}
          </p>
        </div>
      </div>

      <div className="space-y-4 max-w-xl mx-auto">
        <h4 className="text-lg font-semibold text-gray-900">What happens next?</h4>
        <div className="grid grid-cols-1 gap-4 text-sm">
          <div className="flex items-start text-left">
            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
              <span className="text-blue-600 font-bold text-xs">1</span>
            </div>
            <div>
              <p className="font-medium text-gray-900">Server Setup</p>
              <p className="text-gray-600">We'll configure your dedicated HRMS server and database</p>
            </div>
          </div>

          <div className="flex items-start text-left">
            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
              <span className="text-blue-600 font-bold text-xs">2</span>
            </div>
            <div>
              <p className="font-medium text-gray-900">Domain Configuration</p>
              <p className="text-gray-600">
                {domainData.type === 'custom'
                  ? 'We\'ll contact you to set up your custom domain'
                  : 'Your subdomain will be pointed to your HRMS instance'}
              </p>
            </div>
          </div>

          <div className="flex items-start text-left">
            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
              <span className="text-blue-600 font-bold text-xs">3</span>
            </div>
            <div>
              <p className="font-medium text-gray-900">Superadmin Credentials</p>
              <p className="text-gray-600">
                <strong>{adminData.email}</strong> will receive login details via email
              </p>
            </div>
          </div>

          <div className="flex items-start text-left">
            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
              <span className="text-blue-600 font-bold text-xs">4</span>
            </div>
            <div>
              <p className="font-medium text-gray-900">Start Using BestoHRMS</p>
              <p className="text-gray-600">Login and begin configuring your HR processes</p>
            </div>
          </div>
        </div>
      </div>

      <div className="pt-6 sm:pt-8">
        <div className="bg-white border border-gray-200 rounded-xl p-6 max-w-lg mx-auto">
          <h5 className="font-semibold text-gray-900 mb-3">Need Help?</h5>
          <p className="text-sm text-gray-600 mb-4">
            Our support team is ready to assist you with any questions about your new HRMS.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="mailto:support@bestohrms.com"
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors text-center"
            >
              Email Support
            </a>
            <a
              href="tel:+911234567890"
              className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors text-center"
            >
              Call Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );

  // Main Render
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100">
      <ResumeProgressDialog
        show={showResumeDialog}
        savedData={savedProgressData}
        onResume={handleResumeProgress}
        onStartFresh={handleStartFresh}
        onDismiss={() => setShowResumeDialog(false)}
      />

      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-8 sm:mb-12">
          <div className="flex items-center justify-center mb-4 sm:mb-6">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl flex items-center justify-center shadow-xl">
              <Building2 className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            Welcome to <span className="text-blue-600">BestoHRMS</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto px-4">
            Set up your HR management system in just a few simple steps
          </p>
        </div>

        <div className="max-w-4xl mx-auto mb-8 sm:mb-12 px-4">
          <ProgressBar progress={progressPercentage} />
          <div className="text-center mt-4">
            <span className="text-sm font-semibold text-gray-600">
              Step {currentStep} of {STEPS.length} • {progressPercentage}% Complete
            </span>
          </div>
        </div>

        <div className="max-w-6xl mx-auto mb-8 sm:mb-12 px-4">
          <StepIndicator steps={STEPS} currentStep={currentStep} />
        </div>

        {alerts.length > 0 && (
          <div className="max-w-4xl mx-auto mb-8 space-y-4 px-4">
            {alerts.map((alert) => (
              <Alert
                key={alert.id}
                type={alert.type}
                message={alert.message}
                onDismiss={() => removeAlert(alert.id)}
              />
            ))}
          </div>
        )}

        <div className="max-w-6xl mx-auto px-4">
          <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
            <div className="p-6 sm:p-8 md:p-12">
              {currentStep === 1 && renderSuperadminForm()}
              {currentStep === 2 && renderCompanyForm()}
              {currentStep === 3 && renderDomainSetup()}
              {currentStep === 4 && renderPlanSelection()}
              {currentStep === 5 && renderSuccessPage()}
            </div>

            {(currentStep > 1 && currentStep < 5) && (
              <div className="px-6 sm:px-8 md:px-12 pb-6">
                <div className="flex items-center justify-end text-sm text-gray-500">
                  <Save className="w-4 h-4 mr-2" />
                  Progress automatically saved
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="text-center mt-12 sm:mt-16 text-gray-500 px-4">
          <p className="text-sm">
            Need help? Contact our support team at{" "}
            <a href="mailto:support@bestohrms.com" className="text-blue-600 hover:text-blue-700 transition-colors">
              support@bestohrms.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterCompanyFlow;